import { type TFunction } from "i18next";
import { z } from "zod";
import { validateQuantity, validateRts, validateUnit } from "./sharedValidators";

const LANG_BASE = "vessel-call.port-services";

type DeSlopingData = {
  serviceCategory?: string;
  deSlopingType?: string;
  berthOrAnchorageCode?: string;
  bunkerBargeIMO?: string;
  bunkerBargeName?: string;
  bunkerBargeLookupPending?: boolean;
  bunkerBargeLookupErrorMessageKey?: string;
  deSlopingCompanyName?: string;
  quantity?: string;
  unitOfMeasurement?: string;
  rts?: string | Date | null;
};

const isBlank = (value: unknown) =>
  value === undefined || value === null || value.toString().trim() === "";

export const validateDeSlopingServices = (
  data: DeSlopingData,
  ctx: z.RefinementCtx,
  t: TFunction,
  currentVesselImoNumber?: string,
) => {
  const requiredFields: Array<{
    key: keyof DeSlopingData;
    labelKey: string;
  }> = [
    { key: "serviceCategory", labelKey: "service-category" },
    { key: "deSlopingType", labelKey: "de-sloping-type" },
    { key: "berthOrAnchorageCode", labelKey: "berth-anchorage-area-location" },
    { key: "bunkerBargeIMO", labelKey: "bunker-barge-imo" },
    { key: "deSlopingCompanyName", labelKey: "de-sloping-company-name" },
  ];

  requiredFields.forEach(({ key, labelKey }) => {
    if (isBlank(data[key])) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: t("form-validation.required", {
          label: t(`${LANG_BASE}.fields.${labelKey}`),
        }),
        path: [key],
      });
    }
  });

  const bunkerBargeIMO = data.bunkerBargeIMO?.trim() ?? "";
  const hasBunkerBargeName = !isBlank(data.bunkerBargeName);
  const isBunkerBargeLookupPending = data.bunkerBargeLookupPending === true;
  const lookupErrorMessageKey =
    data.bunkerBargeLookupErrorMessageKey?.trim() ||
    "form-validation.valid-bunker-barge-imo-required";

  if (bunkerBargeIMO && !/^\d+$/.test(bunkerBargeIMO)) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: t("form-validation.invalid-number", {
        label: t(`${LANG_BASE}.fields.bunker-barge-imo`),
      }),
      path: ["bunkerBargeIMO"],
    });
  }

  if (bunkerBargeIMO && bunkerBargeIMO.length > 8) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: t("form-validation.max-length", {
        label: t(`${LANG_BASE}.fields.bunker-barge-imo`),
        max: 8,
      }),
      path: ["bunkerBargeIMO"],
    });
  }

  if (bunkerBargeIMO && bunkerBargeIMO === currentVesselImoNumber?.toString().trim()) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: t("form-validation.cannot-be-same-imo"),
      path: ["bunkerBargeIMO"],
    });
  } else if (
    bunkerBargeIMO &&
    bunkerBargeIMO.length <= 8 &&
    !hasBunkerBargeName &&
    !isBunkerBargeLookupPending
  ) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: t(lookupErrorMessageKey),
      path: ["bunkerBargeIMO"],
    });
  }

  validateQuantity(data.quantity, ctx, t);

  if (data.quantity && !/^\d+$/.test(data.quantity)) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: t("form-validation.invalid-number", {
        label: t(`${LANG_BASE}.fields.quantity`),
      }),
      path: ["quantity"],
    });
  }

  validateUnit(data.unitOfMeasurement, ctx, t);
  validateRts(data.rts, ctx, t, 24);
};
